import type { ApiResult } from '../types';
import type { CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import type { CivilLitigationStepsResponse } from '../types/civilLitigationApi';
import type { DocumentChecklistItem, LookupItem } from '../mocks/criminalMasterData';
import type { CivilLitigationWizardStep } from '../pages/wizard/civilLitigation/civilLitigationConstants';
import { DEFAULT_CIVIL_LITIGATION_DRAFT } from '../layouts/CivilLitigationRequestLayout';
import { parseCourtTypesFromApi, resolveDraftCourtTypeFields } from '../utils/courtTypeLookup';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import { cachedCriminalFetch } from './criminalLookupCache';
import {
  buildSaveCivilLitigationPayload,
  caseRequestIdFromResponse,
  mapCivilLitigationStepsToDraft,
  referenceNoFromResponse,
  resolveCivilLitigationDraftResumeStep,
} from './civilLitigationCaseMapper';

export const getFileCategories = criminalService.getFileCategories;
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getCorporateInstitutes = criminalService.getCorporateInstitutes;
export const getDocumentChecklist = criminalService.getDocumentChecklist;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
/** CMS-CRC-30 / CMS-CRC-31 — same common cover note APIs as civil advise */
export {
  getCommonCoverNote as getCivilLitigationCoverNote,
  downloadCommonCoverNotePdf as downloadCivilLitigationCoverNotePdf,
} from './coverNoteRequests';

/** CMS-MC-1 — courts for civil litigation (courtType + optional districtId and provinceId) */
export const getLitigationCourts = criminalService.getCourts;

function civilLitigationNextStepId(row: Record<string, unknown>): string {
  if (typeof row.id === 'string') return row.id.trim();
  if (typeof row.id === 'number' && Number.isFinite(row.id)) return String(row.id);
  if (typeof row.code === 'string') return row.code.trim();
  return '';
}

function parseCivilLitigationNextSteps(data: unknown): LookupItem[] {
  if (!Array.isArray(data)) return [];
  const rows: { order: number; item: LookupItem }[] = [];
  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    if (r.active === false) continue;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id = civilLitigationNextStepId(r);
    if (!name || !id) continue;
    const order =
      typeof r.orderIndex === 'number'
        ? r.orderIndex
        : typeof r.order_index === 'number'
          ? r.order_index
          : 0;
    rows.push({
      order,
      item: {
        id,
        name,
        code: typeof r.code === 'string' ? r.code : undefined,
      },
    });
  }
  return rows.sort((a, b) => a.order - b.order).map((r) => r.item);
}

/** CMS-MCLNS-1 — active civil litigation next steps */
export async function getCivilLitigationNextSteps(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch(
    'civil-litigation-next-steps',
    async () => {
      const res = await caseApiRequest<unknown>('/civil-litigation-next-steps');
      if (!res.ok) return { success: false, message: res.message };
      return { success: true, data: parseCivilLitigationNextSteps(res.data) };
    },
    { cacheWhen: (r) => r.success && r.data.length > 0 },
  );
}

/** Court types — master UUID ids from /court-types */
export async function getLitigationCourtTypes(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch('litigation-court-types', async () => {
    const res = await caseApiRequest<unknown>('/court-types');
    if (!res.ok) return { success: false, message: res.message };
    const rows = parseCourtTypesFromApi(res.data);
    return { success: true, data: rows };
  });
}

async function normalizeCivilLitigationCourtTypeDraft(
  draft: CivilLitigationWizardDraft,
): Promise<CivilLitigationWizardDraft> {
  if (!draft.courtTypeId.trim()) return draft;
  const courtTypesResult = await getLitigationCourtTypes();
  const courtTypes = courtTypesResult.success ? courtTypesResult.data : [];
  const resolved = resolveDraftCourtTypeFields(courtTypes, {
    courtTypeId: draft.courtTypeId,
    courtTypeName: draft.courtTypeName,
  });
  if (
    resolved.courtTypeId === draft.courtTypeId &&
    resolved.courtTypeName === draft.courtTypeName
  ) {
    return draft;
  }
  return { ...draft, ...resolved };
}

export type SaveCivilLitigationResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-CLC-1 — save/update civil litigation steps 1–5 */
export async function saveCivilLitigationRequest(
  draft: CivilLitigationWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: CivilLitigationWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveCivilLitigationResult>> {
  const normalizedDraft = await normalizeCivilLitigationCourtTypeDraft(draft);
  const body = buildSaveCivilLitigationPayload(normalizedDraft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<CivilLitigationStepsResponse>('/civil-litigation/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type CivilLitigationRequestViewData = {
  draft: CivilLitigationWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: CivilLitigationStepsResponse['step_1'];
  step2?: CivilLitigationStepsResponse['step_2'];
  step3?: CivilLitigationStepsResponse['step_3'];
  step4?: CivilLitigationStepsResponse['step_4'];
  step5?: CivilLitigationStepsResponse['step_5'];
};

/** CMS-CLC-2 — full GET payload (wizard resume + view) */
export async function getCivilLitigationRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CivilLitigationRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<CivilLitigationStepsResponse>(
    caseRequestStepsPath(`/civil-litigation/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = await normalizeCivilLitigationCourtTypeDraft(
    mapCivilLitigationStepsToDraft(res.data, {
      ...DEFAULT_CIVIL_LITIGATION_DRAFT,
      draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
    }),
  );

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
      step5: res.data.step_5,
    },
  };
}

/** CMS-CLC-2 — load saved civil litigation steps for continue draft / wizard resume */
export async function getCivilLitigationRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<CivilLitigationWizardDraft | null>> {
  const view = await getCivilLitigationRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-CLC-2 — which wizard step to open when continuing a saved draft */
export async function getCivilLitigationDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: CivilLitigationWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<CivilLitigationStepsResponse>(
    `/civil-litigation/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveCivilLitigationDraftResumeStep(res.data), requestId },
  };
}

export async function submitCivilLitigationRequest(
  draft: CivilLitigationWizardDraft,
): Promise<ApiResult<SaveCivilLitigationResult>> {
  return saveCivilLitigationRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}
